import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { 
  Plus,
  Home,
  Building2,
  Users,
  TrendingUp,
  Clock,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import AdminSidebar from '@/components/admin/AdminSidebar';

const AdminDashboard = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchData();
    }
  }, [admin]);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/admin/leads?status=new`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin) return null;

  const formTypeLabels = {
    contact: 'Contact',
    off_market: 'Off Market',
    estimation: 'Estimation',
    property_inquiry: 'Property Inquiry',
    acheter_search: 'Acheter Search',
    louer_search: 'Louer Search',
    recherche: 'Recherche'
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          newLeadsCount={stats?.leads?.new || 0}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10 min-h-screen">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="text-2xl font-heading text-[#1A1A1A]">Dashboard</h1>
              <p className="text-sm text-[#6B6B6B] mt-1">Welcome back, {admin.name}</p>
            </div>
            <Link
              to="/admin/properties/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded-sm hover:bg-[#333] transition-colors"
              data-testid="add-property-btn"
            >
              <Plus size={16} />
              Add Property
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-white p-6 rounded-sm border border-[#E0DED8]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#F5F3EF] rounded-full flex items-center justify-center">
                  <Building2 size={18} className="text-[#8B7355]" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B]">Total Properties</span>
              </div>
              <p className="text-3xl font-heading text-[#1A1A1A]" data-testid="total-properties">
                {stats?.properties?.total || 0}
              </p>
            </div>

            <div className="bg-white p-6 rounded-sm border border-[#E0DED8]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#F5F3EF] rounded-full flex items-center justify-center">
                  <Home size={18} className="text-[#8B7355]" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B]">For Sale</span>
              </div>
              <p className="text-3xl font-heading text-[#1A1A1A]" data-testid="sale-properties">
                {stats?.properties?.sale || 0}
              </p>
            </div>

            <div className="bg-white p-6 rounded-sm border border-[#E0DED8]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#F5F3EF] rounded-full flex items-center justify-center">
                  <TrendingUp size={18} className="text-[#8B7355]" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B]">For Rent</span>
              </div>
              <p className="text-3xl font-heading text-[#1A1A1A]" data-testid="rent-properties">
                {stats?.properties?.rent || 0}
              </p>
            </div>

            <div className="bg-white p-6 rounded-sm border border-[#E0DED8]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#F5F3EF] rounded-full flex items-center justify-center">
                  <Users size={18} className="text-[#8B7355]" />
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B]">New Leads</span>
              </div>
              <p className="text-3xl font-heading text-[#1A1A1A]" data-testid="new-leads">
                {stats?.leads?.new || 0}
              </p>
            </div>
          </div>

          {/* Recent Leads */}
          <div className="bg-white rounded-sm border border-[#E0DED8]">
            <div className="px-6 py-4 border-b border-[#E0DED8] flex items-center justify-between">
              <h2 className="font-heading text-lg text-[#1A1A1A]">Recent Leads</h2>
              <Link
                to="/admin/leads"
                className="text-sm text-[#8B7355] hover:text-[#6B5A45] flex items-center gap-1"
              >
                View All <ChevronRight size={14} />
              </Link>
            </div>
            
            {recentLeads.length === 0 ? (
              <div className="p-10 text-center text-[#9B9B9B]">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p>No new leads yet</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E0DED8]">
                {recentLeads.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/admin/leads/${lead.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-[#F5F3EF] transition-colors"
                  >
                    <div>
                      <p className="font-medium text-[#1A1A1A]">
                        {lead.first_name} {lead.last_name || lead.name || 'Unknown'}
                      </p>
                      <p className="text-sm text-[#6B6B6B]">{lead.email}</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-2 py-1 text-xs bg-[#F5F3EF] text-[#6B6B6B] rounded">
                        {formTypeLabels[lead.form_type] || lead.form_type}
                      </span>
                      <p className="text-xs text-[#9B9B9B] mt-1 flex items-center gap-1 justify-end">
                        <Clock size={12} />
                        {new Date(lead.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
